//frontend/src/components/layout/CompanyLayout.jsx
import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { notificationAPI, companyAPI } from '../../services/api';
import getImageUrl from '../../utils/imageUrl';

const CompanyLayout = ({ children }) => {
  const { user, logout, hasPermission } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // Notification state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Modal states
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // Profile form state
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
    avatar: null
  });
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);

  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);

  // Notification functions
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await notificationAPI.getNotifications({ limit: 20 });
      if (response.data.success) {
        setNotifications(response.data.data.notifications || []);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const response = await notificationAPI.getUnreadCount();
      if (response.data.success) {
        setUnreadCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
    }
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await notificationAPI.deleteNotification(notificationId);
      const deletedNotif = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      if (deletedNotif && !deletedNotif.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  };

  const clearReadNotifications = async () => {
    try {
      const readNotifications = notifications.filter(n => n.read);
      for (const notif of readNotifications) {
        await notificationAPI.deleteNotification(notif.id);
      }
      setNotifications(prev => prev.filter(n => !n.read));
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  const openNotificationDetail = (notif) => {
    if (!notif.read) markAsRead(notif.id);
    setSelectedNotification(notif);
    setShowNotificationModal(true);
    setNotificationOpen(false);
  };

  const getNotificationIcon = (type) => {
    const icons = {
      'new_delivery': '📦', 'delivery_created': '📦', 'cod_payment_confirmed': '✅',
      'cod_payment_rejected': '❌', 'driver_shift_completed': '🏁', 'invoice': '💵',
      'system_announcement': '📢', 'info': 'ℹ️'
    };
    return icons[type] || '🔔';
  };

  const getNotificationTypeLabel = (type) => {
    const labels = {
      'new_delivery': 'Шинэ хүргэлт', 'delivery_created': 'Хүргэлт үүссэн',
      'cod_payment_confirmed': 'COD баталгаажсан', 'cod_payment_rejected': 'COD татгалзсан',
      'driver_shift_completed': 'Жолооч дууссан', 'invoice': 'Төлбөрийн мэдэгдэл',
      'system_announcement': 'Системийн мэдэгдэл', 'info': 'Мэдээлэл'
    };
    return labels[type] || 'Мэдэгдэл';
  };

  const getNotificationColor = (type) => {
    const colors = {
      'new_delivery': 'bg-blue-500', 'delivery_created': 'bg-blue-500',
      'cod_payment_confirmed': 'bg-green-500', 'cod_payment_rejected': 'bg-red-500',
      'driver_shift_completed': 'bg-purple-500', 'invoice': 'bg-yellow-500',
      'system_announcement': 'bg-indigo-500', 'info': 'bg-gray-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const timeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const seconds = Math.floor((now - date) / 1000);
    if (seconds < 60) return 'Дөнгөж сая';
    if (seconds < 3600) return Math.floor(seconds / 60) + ' мин өмнө';
    if (seconds < 86400) return Math.floor(seconds / 3600) + ' цаг өмнө';
    if (seconds < 604800) return Math.floor(seconds / 86400) + ' өдөр өмнө';
    return date.toLocaleDateString('mn-MN');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('mn-MN', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Profile modal functions
  const openProfileModal = () => {
    setProfileData({
      full_name: user?.full_name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      avatar: null
    });
    setAvatarPreview(user?.avatar_url ? getImageUrl(user.avatar_url) : null);
    setShowProfileModal(true);
    setUserMenuOpen(false);
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileData(prev => ({ ...prev, avatar: file }));
      const reader = new FileReader();
      reader.onloadend = () => setAvatarPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async () => {
    setProfileSaving(true);
    try {
      const formData = new FormData();
      formData.append('full_name', profileData.full_name);
      formData.append('email', profileData.email);
      formData.append('phone', profileData.phone || '');
      if (profileData.avatar) {
        formData.append('avatar', profileData.avatar);
      }

      const response = await companyAPI.updateProfile(formData);
      if (response.data.success) {
        alert('Профайл амжилттай шинэчлэгдлээ');
        setShowProfileModal(false);
        window.location.reload();
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setProfileSaving(false);
    }
  };

  // Password modal functions
  const openPasswordModal = () => {
    setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    setShowPasswordModal(true);
    setUserMenuOpen(false);
  };

  const handleSavePassword = async () => {
    if (passwordData.new_password.length < 6) {
      alert('Шинэ нууц үг 6-аас дээш тэмдэгттэй байх ёстой');
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('Шинэ нууц үг таарахгүй байна');
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await companyAPI.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      if (response.data.success) {
        alert('Нууц үг амжилттай солигдлоо');
        setShowPasswordModal(false);
      }
    } catch (error) {
      alert(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setPasswordSaving(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUnreadCount();
    const interval = setInterval(() => fetchUnreadCount(), 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchUnreadCount]);

  useEffect(() => {
    if (notificationOpen) fetchNotifications();
  }, [notificationOpen, fetchNotifications]);

  const allMenuItems = [
    { icon: '📊', label: 'Хянах самбар', path: '/company/dashboard', permission: null },
    { icon: '🏭', label: 'Агуулах', path: '/company/warehouses', permission: 'warehouse.view' },
    { icon: '🛍️', label: 'Онлайн шопууд', path: '/company/shops', permission: 'shop.view' },
    { icon: '📦', label: 'Бараа', path: '/company/products', permission: 'product.view' },
    { icon: '📥', label: 'Орлого', path: '/company/inventory/receipt', permission: 'inventory.receive' },
    { icon: '📤', label: 'Зарлага', path: '/company/inventory/outbound', permission: 'inventory.issue' },
    { icon: '🚚', label: 'Хүргэлт', path: '/company/deliveries', permission: 'delivery.view' },
    { icon: '👨‍✈️', label: 'Жолоочийн удирдлага', path: '/company/drivers', permission: 'driver.view' },
    { icon: '🗺️', label: 'Бүсчлэл', path: '/company/zones', permission: 'delivery.view' },
    { icon: '👥', label: 'Хэрэглэгчид', path: '/company/users', permission: 'user.view' },
    { icon: '💰', label: 'Төлбөр тооцоо', path: '/company/settlements', permission: 'financial.view' },
    { icon: '📈', label: 'Тайлан', path: '/company/reports', permission: 'financial.reports', badge: 'Soon' },
    { icon: '⚙️', label: 'Тохиргоо', path: '/company/settings', permission: 'settings.view' }
  ];

  const menuItems = allMenuItems.filter(item => {
    if (!item.permission) return true;
    if (user?.is_admin) return true;
    return hasPermission(item.permission);
  });

  const handleLogout = () => {
    if (confirm('Гарахдаа итгэлтэй байна уу?')) {
      logout();
      navigate('/login');
    }
  };

  const companyName = user?.company?.company_name || 'Компани';
  const companyLogo = user?.company?.logo_thumbnail || user?.company?.logo_url;
  const userName = user?.full_name || user?.username || 'Хэрэглэгч';
  const userAvatar = user?.avatar_url;

  const getPageTitle = () => {
    const pathTitles = {
      '/company/dashboard': 'Хянах самбар', '/company/warehouses': 'Агуулах',
      '/company/inventory/receipt': 'Орлого удирдах', '/company/inventory/outbound': 'Зарлага удирдах',
      '/company/shops': 'Онлайн шопууд', '/company/categories': 'Барааны удирдлага',
      '/company/products': 'Барааны удирдлага', '/company/deliveries': 'Хүргэлтийн удирдлага',
      '/company/drivers': 'Жолоочийн удирдлага', '/company/zones': 'Хүргэлтийн бүсчлэл',
      '/company/users': 'Хэрэглэгчид', '/company/settlements': 'Төлбөр тооцоо',
      '/company/reports': 'Тайлан', '/company/settings': 'Тохиргоо'
    };
    if (location.pathname.includes('/warehouses/') && location.pathname !== '/company/warehouses') return 'Агуулахын дэлгэрэнгүй';
    if (location.pathname.includes('/shops/') && location.pathname !== '/company/shops') return 'Шопын дэлгэрэнгүй';
    if (location.pathname.includes('/products/') && location.pathname.includes('/variants')) return 'Барааны варианууд';
    if (location.pathname.includes('/deliveries/') && location.pathname !== '/company/deliveries') return 'Хүргэлтийн дэлгэрэнгүй';
    if (location.pathname.includes('/drivers/') && location.pathname !== '/company/drivers') return 'Жолоочийн дэлгэрэнгүй';
    return pathTitles[location.pathname] || 'Компанийн панел';
  };

  const getPageSubtitle = () => {
    const subtitles = {
      '/company/dashboard': 'Компанийн ерөнхий мэдээлэл', '/company/warehouses': 'Агуулахын удирдлага',
      '/company/inventory/receipt': 'Орлого бүртгэх болон түүх харах', '/company/inventory/outbound': 'Зарлага бүртгэх болон түүх харах',
      '/company/shops': 'Онлайн шопуудын удирдлага', '/company/categories': 'Ангилал болон барааны удирдлага',
      '/company/products': 'Ангилал болон барааны удирдлага', '/company/deliveries': 'Хүргэлт хувиарлах болон төлөв харах',
      '/company/drivers': 'Жолооч бүртгэх, засах, статистик харах', '/company/zones': 'Бүс үүсгэх, засах, дүүрэг холбох',
      '/company/users': 'Хэрэглэгчдийн удирдлага', '/company/settlements': 'Шопуудын болон системийн төлбөр тооцоо',
      '/company/reports': 'Тайлан ба статистик', '/company/settings': 'Системийн тохиргоо'
    };
    if (location.pathname.includes('/products/') && location.pathname.includes('/variants')) return 'Барааны сонголтууд болон үнэ';
    return subtitles[location.pathname] || '';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <aside className={`fixed inset-y-0 left-0 z-50 bg-white border-r border-gray-200 transition-all duration-300 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 ${sidebarCollapsed ? 'lg:w-20' : 'lg:w-64'}`}>
          <div className="flex flex-col h-full">
            <div className="flex-shrink-0 border-b border-gray-200 p-4">
              <button onClick={() => setMobileMenuOpen(false)} className="lg:hidden absolute top-4 right-4 text-gray-500 hover:text-gray-700">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
              {!sidebarCollapsed ? (
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-200 flex-shrink-0">
                    {companyLogo ? <img src={getImageUrl(companyLogo)} alt={companyName} className="max-w-full max-h-full object-contain p-1.5" /> : <span className="text-2xl">🏢</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h1 className="text-sm font-bold text-gray-900 leading-tight mb-1 break-words">{companyName}</h1>
                    <p className="text-xs text-gray-500 font-medium">Админ панел</p>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center">
                  <div className="w-12 h-12 bg-gray-50 rounded-lg flex items-center justify-center border border-gray-200">
                    {companyLogo ? <img src={getImageUrl(companyLogo)} alt={companyName} className="max-w-full max-h-full object-contain p-1.5" /> : <span className="text-2xl">🏢</span>}
                  </div>
                </div>
              )}
            </div>

            <nav className="flex-1 px-3 py-4 overflow-y-auto">
              <div className="space-y-1">
                {menuItems.map((item, index) => {
                  const isActive = location.pathname === item.path || 
                    (item.path === '/company/warehouses' && location.pathname.startsWith('/company/warehouses/')) ||
                    (item.path === '/company/deliveries' && location.pathname.startsWith('/company/deliveries/')) ||
                    (item.path === '/company/drivers' && location.pathname.startsWith('/company/drivers/')) ||
                    (item.path === '/company/zones' && location.pathname.startsWith('/company/zones')) ||                   
                    (item.path === '/company/settlements' && location.pathname.startsWith('/company/settlements')) || 
                    (item.path === '/company/products' && (location.pathname === '/company/products' || location.pathname === '/company/categories' || location.pathname.includes('/products/')));
                  
                  return (
                    <Link key={index} to={item.path} onClick={() => setMobileMenuOpen(false)} 
                      className={`flex items-center gap-3 px-3 py-3 rounded-lg transition group relative ${isActive ? 'bg-primary-50 text-primary-600' : 'text-gray-700 hover:bg-gray-50'} ${item.badge ? 'opacity-60 cursor-not-allowed' : ''}`}
                      title={sidebarCollapsed ? item.label : ''}
                      {...(item.badge && { onClick: (e) => e.preventDefault() })}
                    >
                      <span className="text-xl flex-shrink-0">{item.icon}</span>
                      {!sidebarCollapsed && (
                        <>
                          <span className={`text-sm font-medium flex-1 ${isActive ? 'font-semibold' : ''}`}>{item.label}</span>
                          {item.badge && <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-semibold rounded-full">{item.badge}</span>}
                        </>
                      )}
                    </Link>
                  );
                })}
              </div>
            </nav>

            <div className="flex-shrink-0 p-3 border-t border-gray-200">
              {!sidebarCollapsed ? (
                <>
                  <div className="flex items-center gap-3 px-3 py-3 bg-gray-50 rounded-lg mb-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {userAvatar ? <img src={getImageUrl(userAvatar)} alt={userName} className="w-full h-full object-cover" /> : <span className="text-base font-semibold text-primary-600">{userName.charAt(0)}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{userName}</p>
                      <p className="text-xs text-gray-500">Компанийн админ</p>
                    </div>
                  </div>
                  <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 px-3 py-3 text-red-600 hover:bg-red-50 rounded-lg transition">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                    <span className="text-sm font-medium">Гарах</span>
                  </button>
                </>
              ) : (
                <>
                  <div className="flex justify-center mb-3">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                      {userAvatar ? <img src={getImageUrl(userAvatar)} alt={userName} className="w-full h-full object-cover" /> : <span className="text-sm font-semibold text-primary-600">{userName.charAt(0)}</span>}
                    </div>
                  </div>
                  <button onClick={handleLogout} className="w-full flex items-center justify-center px-2 py-3 text-red-600 hover:bg-red-50 rounded-lg transition" title="Гарах">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                  </button>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          {/* Header */}
          <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 lg:px-6 h-20 flex items-center justify-between sticky top-0 z-40">
            <div className="flex items-center gap-4 flex-1">
              <button onClick={() => setSidebarCollapsed(!sidebarCollapsed)} className="hidden lg:flex w-10 h-10 items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
              <button onClick={() => setMobileMenuOpen(true)} className="lg:hidden w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">{getPageTitle()}</h1>
                {getPageSubtitle() && <p className="text-sm text-gray-500 mt-0.5">{getPageSubtitle()}</p>}
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="hidden md:flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                <span className="text-sm">📅</span>
                <span className="text-sm font-medium text-gray-700">{new Date().toLocaleDateString('mn-MN', { month: 'long', day: 'numeric' })}</span>
              </div>

              {/* Notification Dropdown */}
              <div className="relative">
                <button onClick={() => setNotificationOpen(!notificationOpen)} className="relative w-10 h-10 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded-lg transition">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                  {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </button>

                {notificationOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setNotificationOpen(false)}></div>
                    <div className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                      <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-5 py-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" /></svg>
                            </div>
                            <div>
                              <h3 className="text-white font-semibold text-lg">Мэдэгдэл</h3>
                              <p className="text-white/70 text-xs">{unreadCount > 0 ? `${unreadCount} шинэ мэдэгдэл` : 'Шинэ мэдэгдэл байхгүй'}</p>
                            </div>
                          </div>
                          {unreadCount > 0 && <button onClick={markAllAsRead} className="text-white/80 hover:text-white text-xs font-medium bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-full transition">Бүгдийг уншсан</button>}
                        </div>
                      </div>

                      <div className="max-h-[400px] overflow-y-auto">
                        {notifications.length === 0 ? (
                          <div className="py-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" /></svg>
                            </div>
                            <p className="text-gray-500 font-medium">Мэдэгдэл байхгүй</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {notifications.map((notif) => (
                              <div key={notif.id} className={`group relative hover:bg-gray-50 transition cursor-pointer ${!notif.read ? 'bg-blue-50/50' : ''}`}>
                                <div onClick={() => openNotificationDetail(notif)} className="p-4 flex gap-3">
                                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${getNotificationColor(notif.type)}`}>
                                    <span className="text-xl">{getNotificationIcon(notif.type)}</span>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-0.5">
                                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${!notif.read ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{getNotificationTypeLabel(notif.type)}</span>
                                      {!notif.read && <span className="w-2 h-2 bg-blue-500 rounded-full"></span>}
                                    </div>
                                    <h4 className="text-sm font-semibold text-gray-900 truncate">{notif.title}</h4>
                                    <p className="text-sm text-gray-600 line-clamp-2 mt-0.5">{notif.message}</p>
                                    <p className="text-xs text-gray-400 mt-1.5">{timeAgo(notif.created_at)}</p>
                                  </div>
                                  <button onClick={(e) => { e.stopPropagation(); deleteNotification(notif.id); }} className="opacity-0 group-hover:opacity-100 absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition">✕</button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {notifications.length > 0 && (
                        <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                          {notifications.some(n => n.read) && <button onClick={clearReadNotifications} className="text-xs text-gray-500 hover:text-red-600 font-medium">Уншсаныг устгах</button>}
                          <div className="ml-auto text-xs text-gray-400">Нийт {notifications.length}</div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* User Menu - ШИНЭЧЛЭГДСЭН */}
              <div className="relative">
                <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-lg transition">
                  <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center overflow-hidden">
                    {userAvatar ? <img src={getImageUrl(userAvatar)} alt={userName} className="w-full h-full object-cover" /> : <span className="text-sm font-semibold text-primary-600">{userName.charAt(0)}</span>}
                  </div>
                  <span className="hidden md:block text-sm font-medium text-gray-700">{userName}</span>
                  <svg className="hidden md:block w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>
                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)}></div>
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 z-50">
                      <div className="p-4 border-b border-gray-200">
                        <p className="text-sm font-semibold text-gray-900">{userName}</p>
                        <p className="text-xs text-gray-500">{user?.email || 'Компанийн админ'}</p>
                      </div>
                      <div className="p-2">
                        <button onClick={openProfileModal} className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                          <span className="text-sm font-medium">Профайл засах</span>
                        </button>
                        <button onClick={openPasswordModal} className="w-full flex items-center gap-3 px-3 py-2 text-gray-700 hover:bg-gray-50 rounded-lg transition">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" /></svg>
                          <span className="text-sm font-medium">Нууц үг солих</span>
                        </button>
                      </div>
                      <div className="p-2 border-t border-gray-200">
                        <button onClick={() => { setUserMenuOpen(false); handleLogout(); }} className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                          <span className="text-sm font-medium">Гарах</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 lg:p-6">{children}</main>

          <footer className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-4">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm text-gray-600">
              <p>© 2025 <span className="font-semibold text-gray-900">Pingo Delivery</span>. Бүх эрх хуулиар хамгаалагдсан.</p>
              <div className="flex items-center gap-4">
                <a href="#" className="hover:text-primary-600 transition">Тусламж</a>
                <a href="#" className="hover:text-primary-600 transition">Нууцлал</a>
              </div>
            </div>
          </footer>
        </div>
      </div>

      {mobileMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setMobileMenuOpen(false)}></div>}

      {/* Notification Detail Modal */}
      {showNotificationModal && selectedNotification && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowNotificationModal(false)}></div>
            <div className="relative inline-block w-full max-w-lg bg-white rounded-3xl text-left overflow-hidden shadow-2xl sm:my-8">
              <div className={`px-6 py-6 ${selectedNotification.type === 'cod_payment_confirmed' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : selectedNotification.type === 'cod_payment_rejected' ? 'bg-gradient-to-r from-red-500 to-rose-500' : 'bg-gradient-to-r from-blue-500 to-indigo-500'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center"><span className="text-4xl">{getNotificationIcon(selectedNotification.type)}</span></div>
                    <div>
                      <span className="inline-block px-3 py-1 bg-white/25 text-white text-xs font-semibold rounded-full mb-2">{getNotificationTypeLabel(selectedNotification.type)}</span>
                      <h3 className="text-xl font-bold text-white">{selectedNotification.title}</h3>
                    </div>
                  </div>
                  <button onClick={() => setShowNotificationModal(false)} className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition">✕</button>
                </div>
              </div>
              <div className="px-6 py-6">
                <div className="bg-gray-50 rounded-2xl p-5"><p className="text-gray-700 whitespace-pre-wrap">{selectedNotification.message}</p></div>
                <div className="mt-5 text-sm text-gray-500"><span className="bg-gray-100 px-3 py-2 rounded-lg">{formatDate(selectedNotification.created_at)}</span></div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t flex justify-between">
                <button onClick={() => { deleteNotification(selectedNotification.id); setShowNotificationModal(false); }} className="px-5 py-2.5 text-red-600 hover:bg-red-500 hover:text-white border border-red-200 rounded-xl transition">🗑️ Устгах</button>
                <button onClick={() => setShowNotificationModal(false)} className="px-8 py-2.5 bg-gray-800 text-white rounded-xl">Хаах</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowProfileModal(false)}></div>
            <div className="relative inline-block w-full max-w-md bg-white rounded-2xl text-left overflow-hidden shadow-2xl sm:my-8">
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><span className="text-xl">👤</span></div>
                    <div><h2 className="text-xl font-bold text-white">Профайл засах</h2><p className="text-primary-100 text-sm">Хувийн мэдээллээ шинэчлэх</p></div>
                  </div>
                  <button onClick={() => setShowProfileModal(false)} className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white">✕</button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex flex-col items-center">
                  <div className="relative">
                    <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden border-4 border-white shadow-lg">
                      {avatarPreview ? <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" /> : <span className="text-4xl font-bold text-primary-600">{userName.charAt(0)}</span>}
                    </div>
                    <label className="absolute bottom-0 right-0 w-8 h-8 bg-primary-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-700 shadow-lg">
                      <span className="text-white text-sm">📷</span>
                      <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Зураг солихын тулд дээр дарна уу</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Бүтэн нэр</label>
                  <input type="text" value={profileData.full_name} onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500" placeholder="Овог Нэр" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Имэйл</label>
                  <input type="email" value={profileData.email} onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500" placeholder="email@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Утас</label>
                  <input type="text" value={profileData.phone} onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500" placeholder="99001122" />
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
                <button onClick={() => setShowProfileModal(false)} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium">Болих</button>
                <button onClick={handleSaveProfile} disabled={profileSaving} className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                  {profileSaving ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Хадгалж байна...</> : <>💾 Хадгалах</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowPasswordModal(false)}></div>
            <div className="relative inline-block w-full max-w-md bg-white rounded-2xl text-left overflow-hidden shadow-2xl sm:my-8">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center"><span className="text-xl">🔑</span></div>
                    <div><h2 className="text-xl font-bold text-white">Нууц үг солих</h2><p className="text-orange-100 text-sm">Аюулгүй байдлаа хангана уу</p></div>
                  </div>
                  <button onClick={() => setShowPasswordModal(false)} className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center text-white">✕</button>
                </div>
              </div>
              <div className="p-6 space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Одоогийн нууц үг</label>
                  <input type="password" value={passwordData.current_password} onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500" placeholder="••••••••" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Шинэ нууц үг</label>
                  <input type="password" value={passwordData.new_password} onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-orange-500" placeholder="••••••••" />
                  <p className="text-xs text-gray-400 mt-1">Хамгийн багадаа 6 тэмдэгт</p>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Нууц үг давтах</label>
                  <input type="password" value={passwordData.confirm_password} onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))} className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-orange-500 ${passwordData.confirm_password && passwordData.confirm_password !== passwordData.new_password ? 'border-red-300 bg-red-50' : 'border-gray-200'}`} placeholder="••••••••" />
                  {passwordData.confirm_password && passwordData.confirm_password !== passwordData.new_password && <p className="text-xs text-red-500 mt-1">Нууц үг таарахгүй байна</p>}
                </div>
              </div>
              <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
                <button onClick={() => setShowPasswordModal(false)} className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium">Болих</button>
                <button onClick={handleSavePassword} disabled={passwordSaving || passwordData.new_password.length < 6 || passwordData.new_password !== passwordData.confirm_password} className="flex-1 px-4 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 font-medium disabled:opacity-50 flex items-center justify-center gap-2">
                  {passwordSaving ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Хадгалж байна...</> : <>🔑 Нууц үг солих</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyLayout;